
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LanguageSelector, { SupportedLanguage } from "@/components/multilingual/LanguageSelector";
import { Button } from "@/components/ui/button";
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { translations, defaultLanguage, LANGUAGE_COOKIE_NAME, detectBrowserLanguage } from "@/lib/i18n";
import { ExternalLink, X } from "lucide-react";
import { DOCUMENT_VERSIONS, ACTIVE_CHANGELOG } from "@/lib/constants/documentVersions";
import { useChangelogVisibility } from "@/hooks/useChangelogVisibility";

const DISMISS_LABEL: Record<SupportedLanguage, string> = {
  en: 'Got it',
  de: 'Verstanden',
  fr: 'Compris',
  es: 'Entendido',
};

const COOKIE_CHANGELOG_COPY: Record<string, { heading: string; body: string }> = {
  en: {
    heading: "What changed on 30 April 2026",
    body: "Editorial cleanup: ephemeral session-storage UI flags are now grouped under one functional category, and the redundant short cookie summary table was removed. No change to what we store or for how long.",
  },
  de: {
    heading: "Was sich am 30. April 2026 geändert hat",
    body: "Redaktionelle Bereinigung: kurzlebige sessionStorage-UI-Marker sind nun unter einer funktionalen Kategorie zusammengefasst, und die redundante Kurzübersicht wurde entfernt. Keine Änderung an dem, was wir speichern, oder wie lange.",
  },
  fr: {
    heading: "Ce qui a changé le 30 avril 2026",
    body: "Nettoyage rédactionnel : les marqueurs UI éphémères en sessionStorage sont désormais regroupés sous une catégorie fonctionnelle unique, et le tableau résumé redondant a été supprimé. Aucun changement sur ce que nous stockons ni pour combien de temps.",
  },
  es: {
    heading: "Qué cambió el 30 de abril de 2026",
    body: "Limpieza editorial: los marcadores efímeros de UI en sessionStorage se agrupan ahora en una única categoría funcional y se eliminó la tabla resumen redundante. Sin cambios en lo que almacenamos ni durante cuánto tiempo.",
  },
};

const formatVersionDate = (version: string, language: string): string => {
  const locale = language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US';
  try {
    return new Date(version).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return version;
  }
};

const CookiePolicy = () => {
  const [language, setLanguage] = useState<SupportedLanguage>(defaultLanguage);
  const changelog = useChangelogVisibility({
    documentType: 'cookiePolicy',
    versionAnchor: ACTIVE_CHANGELOG.versionAnchor,
    expiresAt: ACTIVE_CHANGELOG.expiresAt,
  });

  useEffect(() => {
    // Check saved language preference
    const savedLanguage = localStorage.getItem(LANGUAGE_COOKIE_NAME);
    if (savedLanguage && ['en', 'de', 'fr', 'es'].includes(savedLanguage)) {
      setLanguage(savedLanguage as SupportedLanguage);
    } else {
      // If no saved language, detect browser language
      const browserLang = detectBrowserLanguage();
      setLanguage(browserLang);
      localStorage.setItem(LANGUAGE_COOKIE_NAME, browserLang);
    }
  }, []);
  
  const t = translations.legalPages[language]?.cookiePolicy;
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow pt-20">
        <div className="max-w-4xl mx-auto px-4 pt-8 pb-16">
          <div className="flex flex-wrap items-center justify-between mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t?.breadcrumb}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <div className="mt-2 sm:mt-0">
              <LanguageSelector language={language} onChange={setLanguage} />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold mb-6">{t?.title}</h1>

          {/* Changelog notice for the EU migration */}
          {changelog.visible && (
            <div
              id={`changelog-${ACTIVE_CHANGELOG.versionAnchor}`}
              className="mb-8 p-4 rounded-lg border border-primary/30 bg-primary/5"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="text-lg font-semibold m-0 text-foreground">
                  {COOKIE_CHANGELOG_COPY[language]?.heading ?? COOKIE_CHANGELOG_COPY.en.heading}
                </h2>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={changelog.dismiss}
                  className="shrink-0 -mt-1 -mr-2 h-8"
                >
                  <X className="h-4 w-4 mr-1" />
                  {DISMISS_LABEL[language]}
                </Button>
              </div>
              <p className="text-sm m-0 text-muted-foreground">
                {COOKIE_CHANGELOG_COPY[language]?.body ?? COOKIE_CHANGELOG_COPY.en.body}
              </p>
            </div>
          )}

          <div className="prose prose-slate max-w-none dark:prose-invert">
            {/* Main content sections */}
            {t?.content.map((section, index) => (
              <div key={index}>
                <h2>{section.title}</h2>
                <p className="whitespace-pre-line">{section.text}</p>
              </div>
            ))}
            
            {/* Detailed Cookie Table (6 columns) */}
            {t?.detailedCookieTable && (
              <div className="mt-8">
                <h2>{t.detailedCookieTable.headers[0] ? 
                  (language === 'de' ? 'Detaillierte Cookie-Liste' : 
                   language === 'fr' ? 'Liste Détaillée des Cookies' : 
                   language === 'es' ? 'Lista Detallada de Cookies' : 
                   'Detailed Cookie List') : 'Detailed Cookie List'}</h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-muted">
                        {t.detailedCookieTable.headers.map((header, i) => (
                          <th key={i} className="border border-border p-2 text-left font-semibold">{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {t.detailedCookieTable.rows.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
                          <td className="border border-border p-2 font-mono text-xs">{row.name}</td>
                          <td className="border border-border p-2">{row.provider}</td>
                          <td className="border border-border p-2">{row.purpose}</td>
                          <td className="border border-border p-2">{row.category}</td>
                          <td className="border border-border p-2">{row.duration}</td>
                          <td className="border border-border p-2 text-xs">{row.legalBasis}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Anonymous Tracking Section */}
            {t?.anonymousTracking && (
              <div className="mt-8">
                <h2>{t.anonymousTracking.title}</h2>
                <p>{t.anonymousTracking.description}</p>
                <p className="font-semibold mt-4">
                  {language === 'de' ? 'Rechtsgrundlage' : 
                   language === 'fr' ? 'Base Juridique' : 
                   language === 'es' ? 'Base Legal' : 
                   'Legal Basis'}: {t.anonymousTracking.legalBasis}
                </p>
                <p className="font-semibold mt-2">
                  {language === 'de' ? 'Erhobene Daten' : 
                   language === 'fr' ? 'Données Collectées' : 
                   language === 'es' ? 'Datos Recopilados' : 
                   'Data Collected'}:
                </p>
                <ul className="list-disc pl-6">
                  {t.anonymousTracking.dataCollected.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Consent-Based Tracking Section */}
            {t?.consentTracking && (
              <div className="mt-8">
                <h2>{t.consentTracking.title}</h2>
                <p>{t.consentTracking.description}</p>
                <p className="font-semibold mt-4">
                  {language === 'de' ? 'Rechtsgrundlage' : 
                   language === 'fr' ? 'Base Juridique' : 
                   language === 'es' ? 'Base Legal' : 
                   'Legal Basis'}: {t.consentTracking.legalBasis}
                </p>
                <p className="font-semibold mt-2">
                  {language === 'de' ? 'Erhobene Daten' : 
                   language === 'fr' ? 'Données Collectées' : 
                   language === 'es' ? 'Datos Recopilados' : 
                   'Data Collected'}:
                </p>
                <ul className="list-disc pl-6">
                  {t.consentTracking.dataCollected.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <p className="mt-2">
                  <strong>
                    {language === 'de' ? 'Speichermechanismus' : 
                     language === 'fr' ? 'Mécanisme de Stockage' : 
                     language === 'es' ? 'Mecanismo de Almacenamiento' : 
                     'Storage Mechanism'}:
                  </strong> {t.consentTracking.storageMechanism}
                </p>
              </div>
            )}
            
            {/* Third-Party Providers Section */}
            {t?.thirdPartyProviders && t.thirdPartyProviders.length > 0 && (
              <div className="mt-8">
                <h2>
                  {language === 'de' ? 'Drittanbieter' : 
                   language === 'fr' ? 'Fournisseurs Tiers' : 
                   language === 'es' ? 'Proveedores Terceros' : 
                   'Third-Party Providers'}
                </h2>
                <p className="mb-4">
                  {language === 'de' ? 'Die folgenden Drittanbieter verarbeiten Daten in unserem Auftrag:' : 
                   language === 'fr' ? 'Les fournisseurs tiers suivants traitent des données en notre nom :' : 
                   language === 'es' ? 'Los siguientes proveedores terceros procesan datos en nuestro nombre:' : 
                   'The following third-party providers process data on our behalf:'}
                </p>
                <p className="mb-4">
                  <Link to="/privacy-policy#sub-processors" className="text-primary hover:underline">
                    {language === 'de' ? 'Vollständige, laufend aktualisierte Liste der Unterauftragsverarbeiter und Empfänger' :
                     language === 'fr' ? 'Liste complète et à jour des sous-traitants et destinataires' :
                     language === 'es' ? 'Lista completa y actualizada de encargados y destinatarios' :
                     'Full, continuously updated list of sub-processors and recipients'}
                  </Link>
                </p>
                {t.thirdPartyProviders.map((provider, i) => (
                  <div key={i} className="mb-4 p-4 bg-muted/50 rounded-lg">
                    <h3 className="font-semibold text-lg mb-2">{provider.name}</h3>
                    <p><strong>
                      {language === 'de' ? 'Zweck' : 
                       language === 'fr' ? 'Objectif' : 
                       language === 'es' ? 'Propósito' : 
                       'Purpose'}:
                    </strong> {provider.purpose}</p>
                    <p><strong>
                      {language === 'de' ? 'Land' : 
                       language === 'fr' ? 'Pays' : 
                       language === 'es' ? 'País' : 
                       'Country'}:
                    </strong> {provider.country}</p>
                    <p><strong>
                      {language === 'de' ? 'Transfergrundlage' : 
                       language === 'fr' ? 'Base de Transfert' : 
                       language === 'es' ? 'Base de Transferencia' : 
                       'Transfer Basis'}:
                    </strong> {provider.transferBasis}</p>
                    <p className="flex items-center gap-1">
                      <strong>
                        {language === 'de' ? 'Datenschutzerklärung' : 
                         language === 'fr' ? 'Politique de Confidentialité' : 
                         language === 'es' ? 'Política de Privacidad' : 
                         'Privacy Policy'}:
                      </strong>
                      <a 
                        href={provider.privacyPolicyUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        {provider.privacyPolicyUrl}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Withdrawal Information Section */}
            {t?.withdrawalInfo && (
              <div className="mt-8">
                <h2>{t.withdrawalInfo.title}</h2>
                <p>{t.withdrawalInfo.description}</p>
                <ol className="list-decimal pl-6 mt-4">
                  {t.withdrawalInfo.steps.map((step, i) => (
                    <li key={i} className="mb-2">{step}</li>
                  ))}
                </ol>
              </div>
            )}
            
            {/* Controller Information Section */}
            {t?.controllerInfo && (
              <div className="mt-8">
                <h2>{t.controllerInfo.title}</h2>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="font-semibold">{t.controllerInfo.name}</p>
                  <p className="whitespace-pre-line">{t.controllerInfo.address}</p>
                  <p className="mt-2">
                    <strong>Email:</strong>{' '}
                    <a 
                      href={`mailto:${t.controllerInfo.email}`}
                      className="text-primary hover:underline"
                    >
                      {t.controllerInfo.email}
                    </a>
                  </p>
                  {t.controllerInfo.phone && (
                    <p>
                      <strong>
                        {language === 'de' ? 'Telefon' : 
                         language === 'fr' ? 'Téléphone' : 
                         language === 'es' ? 'Teléfono' : 
                         'Phone'}:
                      </strong> {t.controllerInfo.phone}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Last Updated — single source of truth via DOCUMENT_VERSIONS */}
            <div className="mt-8 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                {language === 'de' ? 'Zuletzt aktualisiert' :
                 language === 'fr' ? 'Dernière mise à jour' :
                 language === 'es' ? 'Última actualización' :
                 'Last updated'}: {formatVersionDate(DOCUMENT_VERSIONS.cookiePolicy, language)}
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CookiePolicy;
